/* eslint-disable complexity */
import React from 'react';
import PropTypes from 'prop-types';
import { UX2, constants as coreConstants } from '@wsb/guac-widget-core';
import DataAid from '../constants/data-aids';
import { updateSpot, openDeviceMap } from './mapUtil';
import { loadMapBoxLibrary, initializeGLMap } from './mapBoxClientUtil';
import { deviceDetector } from '../utils/helper';
import ExpandIcon from './expandIcon';
import { noop, throttle } from 'lodash';
import StaticMap from './staticMap';
import FullScreenOverlay from './FullScreenOverlay';

class MapboxClient extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      mapboxLoaded: false,
      initialized: false,
      isMobile: deviceDetector.isMobile(),
      isViewPortRender: props.viewDevice === 'MOBILE_RENDER_DEVICE' && !!document.querySelector('.device-chrome'),
      generateMap: !props.lazyLoadMap,
      showMobileOverlay: false
    };
    this.randomId = new Date().getTime().toString(16);
    this.glMap = null;
    this.mapContainer = null;
    this.onScroll = throttle(this.onScroll.bind(this), 250);
  }

  onScroll() {
    if (this.props.lazyLoadMap && this.mapWrapper) {
      // Generate dynamic map after scrolling to within 50px of the map
      const offset = 50;
      const { top } = this.mapWrapper.getBoundingClientRect();
      const windowHeight = window.innerHeight + offset;
      const generateMap = top - windowHeight <= 0;
      if (generateMap) {
        this.stopListening();
        this.setState({ generateMap });
      }
    }
  }

  stopListening() {
    window.removeEventListener('scroll', this.onScroll, { passive: true });
    window.removeEventListener('resize', this.onScroll, { passive: true });
    if (this.renderContainer) this.renderContainer.removeEventListener('scroll', this.onScroll, { passive: true });
  }

  componentDidMount() {
    const { lazyLoadMap, renderMode } = this.props;

    if (lazyLoadMap) {
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.renderContainer = this.renderContainer ||
        (renderMode !== coreConstants.renderModes.PUBLISH && document.getElementById('render-container'));
      if (this.renderContainer) {
        this.renderContainer.addEventListener('scroll', this.onScroll, { passive: true });
      }

      // execute onScroll initially to cover scenarios where the contact widget is at the top
      this.onScroll();
    }
    loadMapBoxLibrary(this.generateMap);
  }

  componentWillUnmount() {
    if (this.glMap) {
      this.removeMapFromGlobalSpace(this.glMap);
      this.glMap.remove();
    }
    this.stopListening();
  }

  componentDidUpdate(prevProps) {
    if (!this.glMap) return void loadMapBoxLibrary(this.generateMap);

    const { lat, lon } = this.props;
    if (lat && lon && (lat !== prevProps.lat || lon !== prevProps.lon)) {
      updateSpot({
        map: this.glMap,
        lat,
        lon
      });
    }
    setTimeout(this.glMap.resize.bind(this.glMap), 500);
  }

  toggleMobileOverlay = () => {
    this.setState({
      showMobileOverlay: !this.state.showMobileOverlay
    });
  }

  generateMap = () => {
    if (!this.state.generateMap) {
      return null;
    }

    const { lat, lon, zoom, viewDevice, mapboxAccessToken, mapboxStyleUrl, renderMode } = this.props;
    const isMobileDevice = (viewDevice && viewDevice.match(/mobile/i)) ||
      (renderMode === coreConstants.renderModes.PUBLISH && deviceDetector.isMobile());

    if (!lat || !lon) return;
    this.glMap = initializeGLMap({
      container: this.mapContainer,
      interactive: !this.state.isRouteMobilePreviewPublish && !isMobileDevice,
      scrollZoom: false,
      lat,
      lon,
      zoom,
      accessToken: mapboxAccessToken,
      styleUrl: mapboxStyleUrl
    });
    if (this.glMap && typeof window !== 'undefined') {
      this.putMapToGlobalSpace(this.glMap);
    }
    if (!this.state.initialized) {
      this.setState({ initialized: true });
    }
    if (this.glMap) {
      this.setState({ mapboxLoaded: true });
    }
  }

  putMapToGlobalSpace(map) {
    window.wsb = window.wsb || {};
    window.wsb.contact = window.wsb.contact || {};
    window.wsb.contact.maps = window.wsb.contact.maps || [];
    window.wsb.contact.handleMapResize = window.wsb.contact.handleMapResize || function () {
      window.requestAnimationFrame(() => {
        window.wsb.contact.maps.forEach(mapItem => {
          mapItem.resize();
        });
      });
    };

    window.wsb.contact.maps.push(map);
  }

  removeMapFromGlobalSpace(map) {
    const maps = window.wsb.contact.maps;
    const index = maps.indexOf(map);
    if (index !== -1) {
      maps.splice(index, 1);
    }
  }

  renderFullScreenMap = (container) => {
    const { lat, lon, zoom, mapboxAccessToken, mapboxStyleUrl } = this.props;
    initializeGLMap({
      container,
      interactive: true,
      lat,
      lon,
      zoom,
      scrollZoom: true,
      accessToken: mapboxAccessToken,
      styleUrl: mapboxStyleUrl
    });
  }

  handleGetDirections = () => {
    const { address } = this.props;
    openDeviceMap(address);
  }

  render() {
    const {
      lat,
      lon,
      zoom,
      category,
      section,
      address,
      staticContent,
      isPublishMode,
      isEditMode,
      viewDevice,
      overrideOverlay } = this.props;
    const { mapboxLoaded, initialized, showMobileOverlay, isViewPortRender, isMobile } = this.state;
    const showMap = Boolean(Number(lat) && Number(lon) && Number(zoom));
    if (!showMap) return null;

    const styles = {
      shared: {
        left: 0,
        top: 0,
        right: 0,
        bottom: 0,
        position: 'absolute',
        cursor: 'pointer',
        display: 'block'
      },
      wrapper: {
        position: 'absolute !important' // Edit mode
      },
      dynamicMap: {
        transform: 'translate3d(0,0,0)'
      }
    };

    const shouldRenderOverlay = overrideOverlay ||
      ((isMobile && !isEditMode && viewDevice !== 'DESKTOP_RENDER_DEVICE') || (isPublishMode && isMobile));
    const openFullScreenOverlay = (shouldRenderOverlay && !isViewPortRender) ?
      this.toggleMobileOverlay : noop;

    const expandIconProps = {
      address,
      staticContent,
      isRouteMobilePreviewPublish: shouldRenderOverlay,
      openFullScreenOverlay
    };

    return (
      <div ref={ el => { this.mapWrapper = el || this.mapWrapper; } }>
        <UX2.Element.Block
          category={ category }
          section={ section }
          style={{ ...styles.shared, ...styles.wrapper }}
        >
          <div
            id={ `mapContainer_${this.randomId}` }
            ref={ element => { this.mapContainer = element; } }
            style={{ ...styles.shared, ...styles.dynamicMap }}
            data-aid={ DataAid.CONTACT_MAP_REND }
            onClick={ openFullScreenOverlay }
          />
          <ExpandIcon { ...expandIconProps } />
          { (initialized && !mapboxLoaded) && <StaticMap /> }
        </UX2.Element.Block>
        { showMobileOverlay &&
          <FullScreenOverlay
            getDirectionsLabel={ staticContent.mapCTA }
            renderMap={ this.renderFullScreenMap }
            handleClose={ this.toggleMobileOverlay }
            handleGetDirections={ this.handleGetDirections }
          /> }
      </div>
    );
  }
}

MapboxClient.propTypes = {
  lat: PropTypes.string,
  lon: PropTypes.string,
  zoom: PropTypes.number,
  viewDevice: PropTypes.string,
  address: PropTypes.string,
  env: PropTypes.string,
  category: PropTypes.string,
  section: PropTypes.string,
  mapboxAccessToken: PropTypes.string,
  mapboxStyleUrl: PropTypes.string,
  staticContent: PropTypes.object.isRequired,
  isPublishMode: PropTypes.bool,
  isEditMode: PropTypes.bool,
  overrideOverlay: PropTypes.bool,
  renderMode: PropTypes.string,
  lazyLoadMap: PropTypes.bool
};

MapboxClient.defaultProps = {
  staticContent: {
    mapCTA: ''
  },
  lazyLoadMap: false
};

export default MapboxClient;
